import { FastColor } from '@ant-design/fast-color';

interface Opts {
  backgroundColor: string;
  color: string;
}

type SeedColors = {
  colorPrimary?: string;
  colorInfo?: string;
  colorSuccess?: string;
  colorWarning?: string;
  colorError?: string;

  colorBgLayout: string;
};

const colorMap = [
  { s: 100, b: 35, h: 0, amount: 20 }, // the darkest
  { s: 100, b: 43, h: 0, amount: 15 },
  { s: 100, b: 51, h: 0, amount: 10 },
  { s: 90, b: 59, h: 0, amount: 5 },
  { s: 77, b: 67, h: 0, amount: 0 },
  { s: 0, b: 0, h: 0, amount: 0 }, // original
  { s: 52, b: 84, h: 0, amount: 0 },
  { s: 46, b: 88, h: 0, amount: 0 },
  { s: 40, b: 92, h: 0, amount: 0 },
  { s: 27, b: 100, h: 0, amount: 0 }, //the lightest
];

function getHue(value: number): number {
  let hue = value;

  if (hue < 0) {
    hue += 360;
  } else if (hue >= 360) {
    hue -= 360;
  }
  return hue;
}

function getPercentage(value: number): number {
  if (value > 100) return 1;
  if (value <= 0) return 0;
  return value / 100;
}

export default function generateColorsDarkPalette({ color, backgroundColor }: Opts): string[] {
  const pColor = new FastColor(color);
  const hsv = pColor.toHsv();

  const colors = colorMap.map(({ s, b, h, amount }, index) => {
    if (index === 5) return color;

    const genColor = new FastColor({
      h: getHue(hsv.h + h),
      s: getPercentage(s),
      v: getPercentage(b),
    });

    return genColor.mix(backgroundColor, amount).toHexString();
  });

  return colors;
}

const populateMapColors = (prefix: string, colors: string[]) => {
  return {
    [prefix + 'Bg']: colors[0],
    [prefix + 'BgHover']: colors[1],
    [prefix + 'Border']: colors[2],
    [prefix + 'BorderHover']: colors[3],
    [prefix + 'Hover']: colors[4],
    [prefix]: colors[5],
    [prefix + 'Active']: colors[6],
    [prefix + 'TextHover']: colors[7],
    [prefix + 'Text']: colors[8],
    [prefix + 'TextActive']: colors[9],
  };
};

export const generateDarkMapTokens = ({
  colorPrimary,
  colorError,
  colorInfo,
  colorSuccess,
  colorWarning,
  colorBgLayout,
}: SeedColors) => {
  const generateColor = (color?: string) =>
    color
      ? generateColorsDarkPalette({
          color,
          backgroundColor: colorBgLayout,
        })
      : undefined;

  const primaryColors = generateColor(colorPrimary);
  const successColors = generateColor(colorSuccess);
  const errorColors = generateColor(colorError);
  const infoColors = generateColor(colorInfo);
  const warningColors = generateColor(colorWarning);

  return {
    colorBgLayout,

    ...(primaryColors ? populateMapColors('colorPrimary', primaryColors) : {}),

    ...(successColors ? populateMapColors('colorSuccess', successColors) : {}),

    ...(errorColors ? populateMapColors('colorError', errorColors) : {}),

    ...(warningColors ? populateMapColors('colorWarning', warningColors) : {}),

    ...(infoColors ? populateMapColors('colorInfo', infoColors) : {}),
  };
};
